"use client";
import {useState, useEffect} from "react";
import Grid from "@mui/material/Grid2";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    IconButton,
    Typography,
    Modal,
    Stack,
    Checkbox,
    Tooltip,
    Chip,
    Badge
} from "@mui/material";
import {formatPrice, formatDealTime, formatPriceRange} from "../../utils";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import DeleteIcon from "@mui/icons-material/Delete";
import { SecurityUtils } from "pp-web";
import { isPostHogActive } from "utils";
import { usePostHog } from "posthog-js/react";
import { FaStar, FaTags } from "react-icons/fa";
import bestSellerBadge from "../../assets/img/best-seller_7251267.png"
import badge from "../../assets/img/seller-badge.png"

import { useMediaQuery } from '@mui/material';
import VariantsDrawer from './VariantsDrawer';
import { RiCheckboxMultipleBlankFill } from "react-icons/ri";



export default function Deal(props) {
    const {
        deal,
        user,
        categories = [],
        updateRecommendation,
        deleteDeal,
        setOpenModal,
        setSelectedDeal,
        onSelect,
        isDealSelected,
        showCheckBox,
        userType = null,
        showVariant
    } = props;
    const hasKeepa = deal?.promoCode?.toUpperCase().includes('KEEPA');
    const [variantsDrawerOpen, setVariantsDrawerOpen] = useState(false);
    const [variantIds, setVariantIds] = useState([]);
    const posthog = usePostHog();
    const dealTime = formatDealTime(deal?.validTill);
    const isMobile = useMediaQuery('(max-width:1023px)');

    useEffect(() => {
        if (deal && deal?.allVariantIds && Array.isArray(deal.allVariantIds)) {
            // Filter out the deal's own product ID from variant IDs
            const dealVariantIds = deal.allVariantIds;
            if (dealVariantIds.length > 1) {
                setVariantIds(dealVariantIds);
            }
            
        }
    }, [deal]);

    // Helper function to format percentage range
    const formatPercentageRange = (minPercentage, maxPercentage, percentage) => {
        if (minPercentage === maxPercentage || !showVariant) {
            return `${percentage}% OFF`;
        }
        return `UPTO ${minPercentage}-${maxPercentage}% OFF`; 
    };



    const handleGetDeal = () => {
        if (isPostHogActive()) {
            posthog.capture("get_deal_click", {
                dealId: deal.productId,
                category: deal.category
            });
        }
        
        // For creator userType, always redirect directly without modal
        if (userType === 'creator') {
            window.open(deal?.url, '_blank', 'noopener,noreferrer');
            if (isPostHogActive()) {
                posthog.capture("redirected_to_amazon", {
                    dealId: deal.productId,
                    category: deal.category
                });
            }
            return;
        }
        
        // For user userType, check modal preference and "don't show again" setting
        const modalPreference = localStorage.getItem('dealModalPreference');
        if (userType === 'user'  && modalPreference !== 'true') {
            setSelectedDeal(deal);
            setOpenModal(true);
        } else {
            window.open(deal?.url, '_blank', 'noopener,noreferrer');
            if (isPostHogActive()) {
                posthog.capture("redirected_to_amazon", {
                    dealId: deal.productId,
                    category: deal.category
                });
            }
        }
    };

    const handleSelect = (e) => {
        e.stopPropagation(); // Prevent event bubbling
        if (onSelect) {
            onSelect(deal.productId);
        }
    };

    const handleVariantsClick = (e) => {
        e.stopPropagation(); // Prevent event bubbling
        setVariantsDrawerOpen(true);
    };





    return (
        <Grid size={{xs: 12, sm: 6, md: 4, lg: 2}} className="user-deal-item">
            <Card sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                position: "relative",
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                    "& .deal-button": {
                        backgroundColor: "var(--theme-dark)",
                        color: "white"
                    },
                    "& .admin-hover-actions": {
                        opacity: 1
                    }
                }
            }}>
                {/* Best Seller Badge */}
                {!hasKeepa && deal?.bestSellerRank && deal?.bestSellerRank < 1000 && (
                    <Box sx={{
                        position: "absolute",
                        top: 8,
                        left: 8,
                        zIndex: 2
                    }}>
                        <img src={bestSellerBadge.src} alt="best-seller-badge" style={{width: '32px', height: '24px'}}/>
                    </Box>
                )}

                {/* Checkbox Container */}
                {showCheckBox && (
                    <Box sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        zIndex: 2
                    }}>
                        <Checkbox
                            checked={isDealSelected(deal.prductId)}
                            onChange={handleSelect}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                color: 'var(--theme-dark)',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                borderRadius: '4px',
                                padding: '2px',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                },
                                '&.Mui-checked': {
                                    color: 'var(--theme-dark)',
                                },
                            }}
                        />
                    </Box>
                )}

                {/* Image Container */}
                <Box sx={{ position: 'relative' }}>
                    {/* Product Image */}
                    <CardMedia
                        component="img"
                        image={deal.image}
                        alt={deal.title}
                        sx={{
                            objectFit: "contain",
                            height: "180px",
                            width: "100%",
                            backgroundColor: "white",
                            padding: "12px",
                            marginTop: "4px"
                        }}
                    />
                </Box>

                <CardContent sx={{
                    flexGrow: 1,
                    p: "12px",
                    "&:last-child": {
                        pb: "12px"
                    }
                }}>
                    {/* Discount and Promo Code */}
                    <Stack direction="row" spacing={1} mb={1} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography
                                component="span"
                                sx={{
                                    color: '#FF4D4F',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                }}
                            >
                                {formatPercentageRange(deal.minPercentage || deal.percentage, deal.maxPercentage || deal.percentage, deal.percentage )}
                            </Typography>
                            {!hasKeepa && (
                                <Typography
                                    component="span"
                                    sx={{
                                        backgroundColor: '#E6F4EA',
                                        color: '#1E8E3E',
                                        fontWeight: 500,
                                        fontSize: '11px',
                                        padding: '2px 6px',
                                        borderRadius: '2px',
                                        display: 'inline-block'
                                    }}
                                >
                                    PROMO CODE
                                </Typography>
                            )}
                        </Stack>
                        {/* Show how many varients are there */}
                        {(variantIds && variantIds.length > 0 && showVariant) && (
                            <Badge 
                                badgeContent={variantIds.length}
                                color="primary"
                                sx={{
                                    '& .MuiBadge-badge': {
                                        fontSize: '10px',
                                        height: '16px',
                                        minWidth: '16px',
                                        backgroundColor: 'var(--theme-dark)',
                                        color: 'white'
                                    }
                                }}
                            >
                                <RiCheckboxMultipleBlankFill 
                                    style={{
                                        color: 'var(--second-color)', 
                                        fontSize: '20px',
                                        cursor: 'pointer'
                                    }} 
                                    onClick={handleVariantsClick}
                                />
                            </Badge>
                        )}
                    </Stack>
                    
                    {/* Title */}
                    <Tooltip title={deal?.title} placement="top-start">
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: "13px",
                                lineHeight: 1.3,
                                height: "34px",
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                mb: 1,
                                color: "black",
                                fontWeight: 650,
                                textAlign: 'left'
                            }}
                        >
                            {deal.title}
                        </Typography>
                    </Tooltip>

                    {/* Rating */}
                    <Stack direction="row" spacing={0.5} alignItems="center" mb={1}>
                        <FaStar style={{color: '#FF9529', fontSize: '14px'}}/>
                        <Typography variant="body2" fontWeight={600} color="black" fontSize="12px">
                            {deal?.ratingStars?.toFixed(1)}
                        </Typography>
                        <Typography variant="body2" color="black" fontSize="12px">
                            ({deal?.ratingCount?.toLocaleString()})
                        </Typography>
                    </Stack>

                    {/* Price */}
                    <Stack direction="row" alignItems="baseline" spacing={1} mb={1}>
                        <Typography variant="h6" fontWeight={650} color="#FF4D4F" fontSize="16px">
                            {formatPriceRange(deal.minPrice || deal.discountedPrice, deal.maxPrice || deal.discountedPrice, deal.discountedPrice, showVariant)}
                        </Typography>
                        <Typography variant="body2" color="black" sx={{textDecoration: "line-through"}} fontSize="13px" fontWeight={500}>
                            {formatPrice(deal.price)}
                        </Typography>
                    </Stack>

                    {/* Deal Time */}
                    <Typography variant="caption" color="black" sx={{ fontSize: '13px', display: 'block', mb: 1, textAlign: 'left' }} fontWeight={500}>
                        {dealTime.text}: <span style={{ color: '#FF4D4F', fontWeight: 500, marginLeft: '4px' }}>{dealTime.time}</span>
                    </Typography>

                    {/* Get Deal Button */}
                    <Button
                        fullWidth
                        variant="outlined"
                        className="deal-button"
                        onClick={handleGetDeal}
                        sx={{
                            textTransform: "none",
                            borderColor: "var(--theme-dark)",
                            color: "var(--theme-dark)",
                            fontWeight: 500,
                            borderRadius: "4px",
                            height: "32px",
                            fontSize: "13px",
                            transition: "all 0.3s ease",
                            "&:hover": {
                                borderColor: "var(--theme-dark)",
                                backgroundColor: "var(--theme-dark)",
                                color: "white"
                            }
                        }}
                    >
                        {userType === 'creator' ? 'Preview Deal' : 'Get Deal'}
                    </Button>
                </CardContent>
            </Card>
            {/* Variants Drawer */}
            {
                variantsDrawerOpen &&
                <VariantsDrawer
                    open={variantsDrawerOpen}
                    onClose={() => setVariantsDrawerOpen(false)}
                    variantIds={variantIds}
                    mainDeal={deal}
                    user={user}
                    categories={categories}
                    userType={userType}
                    setOpenModal={setOpenModal}
                    setSelectedDeal={setSelectedDeal}
                    showCheckBox={showCheckBox}
                    onSelect={onSelect}
                    isDealSelected={isDealSelected}
                />
            }
        </Grid>
    );
}
